import { ref, onUnmounted, type Ref } from 'vue'

/**
 * useSparkCanvas - Celebration sparks and fireworks
 *
 * Creates celebratory sparks shooting up from the bottom
 * plus colorful firework bursts across the screen.
 */

interface Spark {
  x: number
  y: number
  vx: number
  vy: number
  size: number
  life: number
  maxLife: number
  hue: number
  brightness: number
  isFirework: boolean // Part of a firework burst
}

interface Firework {
  x: number
  y: number
  targetY: number
  vy: number
  hue: number
  exploded: boolean
}

export function useSparkCanvas(container: Ref<HTMLElement | null>) {
  const canvas = ref<HTMLCanvasElement | null>(null)
  const ctx = ref<CanvasRenderingContext2D | null>(null)
  const sparks = ref<Spark[]>([])
  const fireworks = ref<Firework[]>([])
  const animationId = ref<number | null>(null)
  const isRunning = ref(false)

  let canvasWidth = 0
  let canvasHeight = 0

  // Configuration
  const config = {
    // Bottom sparks
    maxSparks: 300,
    spawnRate: 4,
    gravity: 0.08,
    sparkSpeed: 8,
    sparkSize: 2,
    // Fireworks
    fireworkInterval: 800, // ms between fireworks
    fireworkParticles: 80, // particles per explosion
  }

  let lastFireworkTime = 0

  /**
   * Initialize the canvas
   */
  function init(): void {
    if (!container.value) return

    const canvasEl = document.createElement('canvas')
    canvasEl.style.cssText = `
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 15;
    `

    container.value.appendChild(canvasEl)
    canvas.value = canvasEl

    resize()

    const context = canvasEl.getContext('2d')
    if (context) {
      ctx.value = context
    }

    window.addEventListener('resize', resize)
  }

  /**
   * Resize canvas
   */
  function resize(): void {
    if (!canvas.value || !container.value) return

    const rect = container.value.getBoundingClientRect()
    canvas.value.width = rect.width
    canvas.value.height = rect.height
    canvasWidth = rect.width
    canvasHeight = rect.height
  }

  /**
   * Spawn a spark from bottom of screen
   */
  function spawnBottomSpark(): void {
    // Spawn along bottom edge
    const spawnX = Math.random() * canvasWidth
    const spawnY = canvasHeight + 5

    // Shoot upward with spread
    const angle = -Math.PI / 2 + (Math.random() - 0.5) * 0.8
    const speed = config.sparkSpeed * (0.8 + Math.random() * 0.5)

    const spark: Spark = {
      x: spawnX,
      y: spawnY,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      size: config.sparkSize * (0.8 + Math.random() * 0.4),
      life: 0,
      maxLife: 80 + Math.random() * 40,
      hue: 35 + Math.random() * 25, // Gold/orange
      brightness: 1.0,
      isFirework: false,
    }

    sparks.value.push(spark)
  }

  /**
   * Launch a firework
   */
  function launchFirework(): void {
    const firework: Firework = {
      x: canvasWidth * (0.2 + Math.random() * 0.6), // Random x in middle 60%
      y: canvasHeight,
      targetY: canvasHeight * (0.15 + Math.random() * 0.35), // Explode in upper portion
      vy: -12 - Math.random() * 4,
      hue: Math.random() * 360, // Any color
      exploded: false,
    }
    fireworks.value.push(firework)
  }

  /**
   * Explode a firework into particles
   */
  function explodeFirework(firework: Firework): void {
    const particleCount = config.fireworkParticles

    for (let i = 0; i < particleCount; i++) {
      const angle = (Math.PI * 2 * i) / particleCount + (Math.random() - 0.5) * 0.5
      const speed = 4 + Math.random() * 4

      const spark: Spark = {
        x: firework.x,
        y: firework.y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        size: 2 + Math.random() * 2,
        life: 0,
        maxLife: 50 + Math.random() * 30,
        hue: firework.hue + (Math.random() - 0.5) * 30,
        brightness: 1.0,
        isFirework: true,
      }

      sparks.value.push(spark)
    }
  }

  /**
   * Update spark physics
   */
  function updateSpark(spark: Spark): boolean {
    spark.life++

    if (spark.life >= spark.maxLife || spark.y > canvasHeight + 50) {
      return false
    }

    // Apply gravity (more for firework particles)
    spark.vy += spark.isFirework ? 0.12 : config.gravity

    // Apply velocity
    spark.x += spark.vx
    spark.y += spark.vy

    // Air resistance
    spark.vx *= 0.98
    spark.vy *= 0.99

    // Fade brightness
    const progress = spark.life / spark.maxLife
    spark.brightness = Math.max(0, 1 - progress * 1.2)

    return spark.brightness > 0
  }

  /**
   * Update firework (before explosion)
   */
  function updateFirework(firework: Firework): boolean {
    if (firework.exploded) return false

    firework.y += firework.vy
    firework.vy += 0.1 // Slight gravity

    // Check if reached target height
    if (firework.y <= firework.targetY) {
      explodeFirework(firework)
      firework.exploded = true
      return false
    }

    return true
  }

  /**
   * Draw a spark with glow
   */
  function drawSpark(spark: Spark): void {
    if (!ctx.value) return

    const context = ctx.value
    const alpha = spark.brightness

    // Glow
    context.beginPath()
    context.arc(spark.x, spark.y, spark.size * 3, 0, Math.PI * 2)
    context.fillStyle = `hsla(${spark.hue}, 100%, 60%, ${alpha * 0.3})`
    context.fill()

    // Core
    context.beginPath()
    context.arc(spark.x, spark.y, spark.size, 0, Math.PI * 2)
    context.fillStyle = `hsla(${spark.hue}, 100%, 70%, ${alpha})`
    context.fill()

    // Bright center
    context.beginPath()
    context.arc(spark.x, spark.y, spark.size * 0.4, 0, Math.PI * 2)
    context.fillStyle = `hsla(${spark.hue}, 50%, 95%, ${alpha})`
    context.fill()
  }

  /**
   * Draw firework trail (before explosion)
   */
  function drawFirework(firework: Firework): void {
    if (!ctx.value) return

    const context = ctx.value

    // Trail
    context.beginPath()
    context.arc(firework.x, firework.y, 3, 0, Math.PI * 2)
    context.fillStyle = `hsla(${firework.hue}, 100%, 80%, 1)`
    context.fill()

    // Glow
    context.beginPath()
    context.arc(firework.x, firework.y, 6, 0, Math.PI * 2)
    context.fillStyle = `hsla(${firework.hue}, 100%, 60%, 0.5)`
    context.fill()
  }

  /**
   * Main animation loop
   */
  function animate(): void {
    if (!isRunning.value || !ctx.value || !canvas.value) return

    const context = ctx.value
    const now = Date.now()

    // Clear canvas
    context.clearRect(0, 0, canvasWidth, canvasHeight)

    // Spawn bottom sparks
    for (let i = 0; i < config.spawnRate; i++) {
      if (sparks.value.length < config.maxSparks) {
        spawnBottomSpark()
      }
    }

    // Launch fireworks periodically
    if (now - lastFireworkTime > config.fireworkInterval) {
      launchFirework()
      lastFireworkTime = now
    }

    // Update and draw fireworks (pre-explosion)
    fireworks.value = fireworks.value.filter((fw) => {
      const alive = updateFirework(fw)
      if (alive) {
        drawFirework(fw)
      }
      return alive
    })

    // Update and draw sparks
    sparks.value = sparks.value.filter((spark) => {
      const alive = updateSpark(spark)
      if (alive) {
        drawSpark(spark)
      }
      return alive
    })

    animationId.value = requestAnimationFrame(animate)
  }

  /**
   * Start the effect
   */
  function start(): void {
    if (isRunning.value) return

    init()
    isRunning.value = true
    lastFireworkTime = Date.now()
    animate()
  }

  /**
   * Unused but kept for API compatibility
   */
  function setSettled(): void {}
  function setEmitPosition(): void {}

  /**
   * Stop the effect
   */
  function stop(): void {
    isRunning.value = false

    if (animationId.value) {
      cancelAnimationFrame(animationId.value)
      animationId.value = null
    }
  }

  /**
   * Cleanup
   */
  function destroy(): void {
    stop()
    window.removeEventListener('resize', resize)

    if (canvas.value && canvas.value.parentNode) {
      canvas.value.parentNode.removeChild(canvas.value)
    }

    canvas.value = null
    ctx.value = null
    sparks.value = []
    fireworks.value = []
  }

  onUnmounted(() => {
    destroy()
  })

  return {
    start,
    stop,
    destroy,
    setEmitPosition,
    setSettled,
    isRunning,
  }
}
